// Pestaña Dispositivos: una tarjeta por equipo enrolado del cliente. Se lee el detalle MDM de cada
// crédito suyo, con la misma caché que la ficha del crédito.
import { useMemo, useState } from 'react';
import { EmptyState, ErrorState, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { useMdmDevicesOfCredits } from '@/domains/credit/mdm/api/mdmDeviceLookup';
import { MdmStatusPill } from '@/domains/credit/mdm/components/MdmStatusPill';
import { readLockPhase } from '@/domains/credit/mdm/logic';
import type { MdmFleetDevice } from '@/domains/credit/mdm/types';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import {
    creditIdsOf,
    customerDevicesReadOf,
    deviceEventsOf,
    deviceModelLabel,
    devicesErrorCopy,
    lockedSinceLabel,
    lockReasonLabel,
    NO_DEVICES_LABEL,
    type CustomerDeviceEvent,
    type CustomerDeviceEventKind,
} from '../lib/customerDevices';
import { CARD, CARD_HEAD, Field, FILE_DETAIL_TRIGGER } from './CustomerFileUi';
import { CustomerDeviceModal } from './CustomerDeviceModal';

/** El color del punto de la línea de tiempo dice de qué hito se trata. */
const EVENT_DOT_CLASS: Record<CustomerDeviceEventKind, string> = {
    enrollment: 'bg-info',
    lock: 'bg-destructive',
    release: 'bg-success',
};

export function CustomerDevicesTab({
    credit,
    onRetryCredit,
}: {
    credit: CustomerCreditDetail;
    /** Reintenta la lectura del crédito: sin sus créditos no hay con qué filtrar la flota. */
    onRetryCredit: () => void;
}) {
    const creditIds = useMemo(() => [...creditIdsOf(credit)], [credit]);
    const fleet = useMdmDevicesOfCredits(creditIds);
    const read = customerDevicesReadOf({ credit, fleet });
    /** El equipo abierto en el modal; `null` es el modal cerrado. */
    const [openDevice, setOpenDevice] = useState<MdmFleetDevice | null>(null);

    if (read.state === 'loading') return <Loading text="Cargando los equipos del cliente…" />;

    if (read.state === 'failed') {
        const copy = devicesErrorCopy(read.source, read.failure);
        return (
            <ErrorState
                title={copy.title}
                description={copy.description}
                onRetry={copy.canRetry ? onRetryCredit : undefined}
            />
        );
    }

    if (read.state === 'empty') return <EmptyState title={NO_DEVICES_LABEL} />;

    return (
        <>
            {read.devices.map((device) => (
                <DeviceCard key={device.imei} device={device} onOpen={setOpenDevice} />
            ))}
            <CustomerDeviceModal
                device={openDevice}
                onClose={() => {
                    setOpenDevice(null);
                }}
            />
        </>
    );
}

function DeviceCard({
    device,
    onOpen,
}: {
    device: MdmFleetDevice;
    onOpen: (device: MdmFleetDevice) => void;
}) {
    const events = deviceEventsOf(device);
    const lockPhase = readLockPhase(device, new Date());

    return (
        <section className={CARD}>
            <div className={cn(CARD_HEAD, 'flex items-center justify-between gap-2')}>
                <h3 className="min-w-0 truncate text-foreground">{deviceModelLabel(device)}</h3>
                <span className="flex flex-none items-center gap-1.5 font-normal normal-case">
                    <MdmStatusPill
                        status={device.mdmStatus}
                        mdmConfirmed={device.mdmConfirmed}
                        lockPhase={lockPhase}
                        lockScheduledFor={device.currentLockScheduledFor}
                    />
                    <span className="font-mono text-[10.5px]">Crédito {device.creditRef}</span>
                </span>
            </div>

            <div className="p-3">
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11.5px]">
                    <Field label="Modelo">{deviceModelLabel(device)}</Field>
                    <Field label="IMEI">
                        <span className="font-mono text-[11px]">{device.imei}</span>
                    </Field>
                    <Field label="Motivo">{lockReasonLabel(device)}</Field>
                    <Field label="Bloqueado desde">{lockedSinceLabel(device, new Date())}</Field>
                </dl>

                <h4 className="mt-4 text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                    Eventos MDM
                </h4>
                <ol className="mt-2 flex flex-col gap-2 border-l-2 border-border pl-3">
                    {events.map((event) => (
                        <DeviceEventItem key={event.kind} event={event} />
                    ))}
                </ol>

                <div className="mt-3 text-right">
                    {/* Con varias tarjetas en el cajón, el nombre accesible dice de qué equipo es. */}
                    <button
                        type="button"
                        aria-label={`Ver detalle del equipo ${device.imei}`}
                        onClick={() => {
                            onOpen(device);
                        }}
                        className={FILE_DETAIL_TRIGGER}
                    >
                        Ver detalle <span aria-hidden>→</span>
                    </button>
                </div>
            </div>
        </section>
    );
}

function DeviceEventItem({ event }: { event: CustomerDeviceEvent }) {
    return (
        <li className="relative pl-2.5">
            <span
                aria-hidden
                className={cn(
                    'absolute -left-[18px] top-1 size-2 rounded-full',
                    EVENT_DOT_CLASS[event.kind],
                )}
            />
            <div className="text-[10px] tabular-nums text-muted-foreground">
                {/* El expediente no fecha todos los hitos, y una fecha inventada es peor que ninguna. */}
                {event.at === undefined ? 'Sin fecha en el expediente' : formatDateMx(event.at)}
            </div>
            <div className="mt-px text-[12px] font-semibold">{event.title}</div>
            <div className="mt-0.5 text-[10.5px] text-muted-foreground">{event.subtitle}</div>
        </li>
    );
}
